<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { VisXYContainer, VisGroupedBar, VisAxis } from '@unovis/vue';
import {
  Card,
  CardHeader,
  CardContent,
} from 'dashboard/components-next/ui-shadcn/card';

const props = defineProps({
  icon: { type: String, required: true },
  title: { type: String, required: true },
  value: { type: [Number, String], required: true },
  series: { type: Array, default: () => [] },
  to: { type: [String, Object], default: null },
  live: { type: Boolean, default: false },
  subtitle: { type: String, default: '' },
});

const { t } = useI18n();

const chartData = computed(() =>
  props.series.map((value, index) => ({ index, value }))
);

const caption = computed(
  () => props.subtitle || t('PAINEL_PRINCIPAL.KPI.UPDATED_NOW')
);

const dayLabels = [
  t('PAINEL_PRINCIPAL.DAYS.MON'),
  t('PAINEL_PRINCIPAL.DAYS.TUE'),
  t('PAINEL_PRINCIPAL.DAYS.WED'),
  t('PAINEL_PRINCIPAL.DAYS.THU'),
  t('PAINEL_PRINCIPAL.DAYS.FRI'),
  t('PAINEL_PRINCIPAL.DAYS.SAT'),
  t('PAINEL_PRINCIPAL.DAYS.SUN'),
];

const x = d => d.index;
const y = d => d.value;
const tickFormat = i => dayLabels[i] || '';
const barColor = () => '#1F61F4';
</script>

<template>
  <Card class="overflow-hidden">
    <CardHeader>
      <div class="flex items-center gap-3 min-w-0">
        <div
          class="size-10 rounded-xl bg-n-alpha-2 dark:bg-n-alpha-1 flex items-center justify-center text-n-slate-12 shrink-0"
        >
          <span class="size-5" :class="[icon]" />
        </div>
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-n-slate-12 truncate">
            {{ title }}
          </h3>
          <p class="text-xs text-n-slate-10 truncate flex items-center gap-1.5">
            <span
              v-if="live"
              class="size-1.5 rounded-full bg-n-teal-9 shrink-0"
            />
            {{ live ? t('PAINEL_PRINCIPAL.KPI.LIVE') : caption }}
          </p>
        </div>
      </div>
      <RouterLink
        v-if="to"
        :to="to"
        :aria-label="t('PAINEL_PRINCIPAL.KPI.GO_TO')"
        class="size-9 rounded-lg bg-n-brand text-white flex items-center justify-center hover:brightness-110 transition shrink-0"
      >
        <span class="i-lucide-arrow-up-right size-5" />
      </RouterLink>
    </CardHeader>
    <CardContent class="pt-0">
      <p class="text-4xl font-bold text-n-slate-12 mb-3">{{ value }}</p>
      <div class="h-24">
        <VisXYContainer
          :data="chartData"
          :height="96"
          :margin="{ top: 4, right: 0, bottom: 18, left: 0 }"
        >
          <VisGroupedBar
            :x="x"
            :y="y"
            :bar-padding="0.35"
            :rounded-corners="6"
            :color="barColor"
          />
          <VisAxis
            type="x"
            :tick-format="tickFormat"
            :grid-line="false"
            :domain-line="false"
            :tick-line="false"
            tick-text-color="rgb(var(--slate-10))"
            :num-ticks="7"
          />
        </VisXYContainer>
      </div>
    </CardContent>
  </Card>
</template>
